import { useEffect, useState } from "react";
import { Nullable } from "../models/shared/Nullable";

interface Props {
    url: string | null;
    method?: string;
    retry?: boolean;
}

export type HttpError = {
    statusCode?: number;
    message?: string;
};

export type ApiResult<T> = [
    T,
    boolean,
    HttpError | undefined
];

const useApi = <T>({
    method = "GET",
    url,
    retry = false
}: Props): ApiResult<Nullable<T>> => {
    const [data, setData] = useState<Nullable<T>>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<HttpError>();

    useEffect(() => {
        if (!url?.length) {
            return;
        }

        const abortController = new AbortController();

        const fetchData = async () => {
            setLoading(true);
            
            try {
                const response = await fetch(
                    url, 
                    {
                        method,
                        signal: abortController.signal
                    });

                if (response.ok) {
                    const dataJson = await response.json();
                    setData(dataJson);
                }
                else {
                    setError({ 
                        statusCode: response.status,
                        message: response.statusText
                    });
                }

                setLoading(false);
            }
            catch (e) {
                console.error(e);

                if (e.name !== "AbortError") {
                    if (e.status) {
                        setError({ 
                            statusCode: e.status,
                            message: e.name
                        });
                    }
                    else {
                        setError({ 
                            statusCode: 500,
                            message: e.name
                        });
                    }

                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            abortController.abort();
        };
    }, [method, retry, url]);

    return [
        data,
        loading,
        error
    ];
};

export default useApi;